import {useEffect} from 'react';
import {useFormikContext} from 'formik';

import {type FulfillmentDetailAddressInput} from '@/graphql/types';
import {
  geocodedAddressResultToFulfillmentDetailAddressInput,
  geocodedAddressResultToString,
} from '@/utils/geocodedAddressResult';
import {type GeocodedAddressResult} from '@/utils/googleAutocomplete';

type SyncFormValues = {
  geocodedAddressResult: GeocodedAddressResult | null;
  address?: FulfillmentDetailAddressInput;
  lastResolvedAddressString: string;
};

/**
 * The geolocation ("use my location") button writes a fully-resolved
 * `geocodedAddressResult`. This mirrors it into the `address` field the keyword
 * typeahead reads, so suggestions reflect the geolocated address. Renders
 * nothing; must live inside the form's <Formik>.
 */
const GeolocationAddressSync: React.FC = () => {
  const {values, setFieldValue} = useFormikContext<SyncFormValues>();
  const {geocodedAddressResult} = values;

  useEffect(() => {
    if (geocodedAddressResult) {
      // Geolocation is a writer of `address` too, so it must also maintain
      // resolveKeywordFormAddress's `lastResolvedAddressString` reuse marker
      // (set to the string the suffix wrote into the input). Otherwise the
      // marker goes stale and a later submit can reuse the wrong address.
      void setFieldValue(
        'address',
        geocodedAddressResultToFulfillmentDetailAddressInput(geocodedAddressResult),
      );
      void setFieldValue(
        'lastResolvedAddressString',
        geocodedAddressResultToString(geocodedAddressResult),
      );
    }
  }, [geocodedAddressResult, setFieldValue]);

  return null;
};

export default GeolocationAddressSync;
